<template>
    <div class="content-wrapper">
        <app-page-top-section :title="$t('import')">
            <a :href="urlGenerator('hotel/house-keeping')" v-if="$can('view_hotel_house_keeping')"
               class="btn btn-success btn-with-shadow mr-1">
                {{ $t('back') }}
            </a>
            <div class="btn-group dropdown ml-2">
                <button class="btn btn-primary rounded-right px-3" data-toggle="dropdown">
                    {{ $t('actions') }} <i class="fas fa-chevron-down fa-sm"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-right">
                    <app-default-button btnClass="dropdown-item" :title="$t('sync')" @click="syncData"/>
                    <app-default-button btnClass="dropdown-item" :title="$t('clean')" @click="cleanData"/>
                    <app-default-button btnClass="dropdown-item" :title="$t('upload')" @click="showModal = true"/>
                </div>
            </div>
        </app-page-top-section>

        <div class="house-keeping-table-import">
            <app-table
                id="house-keeping-table"
                :options="options"
            />
        </div>

        <div v-if="isVisible" class="popup-overlay">
            <div class="popup-content">
                <h3 v-text="$t('import_status')"/>
                <p :class="status === 'success' ? 'success' : 'error'">{{ message }}</p>
                <div v-if="isImporting" class="progress-container">
                    <progress :value="progress" max="100"></progress>
                    <span>{{ progress }}%</span>
                </div>
                <button @click="closePopup" :disabled="isImporting" v-text="$t('close')"/>
            </div>
        </div>

        <modal id="house-keeping-import" v-model="showModal" :title="$t('import')" :preloader="preloader" :loading="loading" @submit="submitData">
            <form ref="form" :data-url='`${TENANT_BASE_URL}app/hotel/house-keeping-import`' @submit.prevent="submitData">
                <app-form-group-selectable
                    type="search-select"
                    :label="$t('hotel')"
                    list-value-field="name"
                    v-model="formData.hotel_id"
                    :chooseLabel="$t('hotel')"
                    :fetch-url="`${TENANT_BASE_URL}selectable/hotel/list`"
                />
                <app-form-group
                    type="switch"
                    :label="$t('confirmed')"
                    v-model="formData.confirmed"
                />
                <div class="form-group mb-5">
                    <label>{{ $t('file') }}</label>
                    <div class="custom-file" style="height: 44px;">
                        <input type="file" id="file" v-on:change="change" class="custom-file-input" accept=".csv">
                        <label for="file" class="custom-file-label" v-text="placeholder" style="padding: 10px 14px !important;"/>
                    </div>
                </div>
                <div class="columns">
                    <div v-for="column in columns" class="form-group row align-items-center">
                        <div class="col-5" v-text="column.label"/>
                        <div class="col-7">
                            <select class="form-control" v-model="column.index">
                                <option value="">---</option>
                                <option v-for="header in headers" :value="header.id" v-text="header.label"/>
                            </select>
                        </div>
                    </div>
                </div>
            </form>
        </modal>
    </div>
</template>

<style>
.house-keeping-table-import .table .datatable-td {
    white-space : nowrap;
}
</style>

<style scoped>
.popup-overlay {
    position         : fixed;
    top              : 0;
    left             : 0;
    width            : 100%;
    height           : 100%;
    background-color : rgba(0, 0, 0, 0.5);
    display          : flex;
    justify-content  : center;
    align-items      : center;
    z-index          : 9999;
}

.popup-overlay .popup-content {
    background-color : white;
    padding          : 20px;
    border-radius    : 8px;
    width            : 400px;
    text-align       : center;
    box-shadow       : 0 4px 8px rgba(0, 0, 0, 0.2);
}

.popup-overlay h3 {
    margin-bottom : 30px;
}

.popup-overlay .success {
    color : green;
}

.popup-overlay .error {
    color : red;
}

.popup-overlay .progress-container {
    margin : 10px 0;
}

.popup-overlay progress {
    width         : 100%;
    height        : 20px;
    border-radius : 4px;
}

.popup-overlay progress::-webkit-progress-bar {
    background-color : #f3f3f3;
    border-radius    : 4px;
}

.popup-overlay progress::-webkit-progress-value {
    background-color : #007bff;
    border-radius    : 4px;
    transition       : width 0.3s ease;
}

.popup-overlay progress::-moz-progress-bar {
    background-color : #007bff;
    border-radius    : 4px;
}

.popup-overlay button {
    margin           : 5px;
    padding          : 8px 16px;
    background-color : #007bff;
    color            : white;
    border           : none;
    border-radius    : 4px;
    cursor           : pointer;
}

.popup-overlay button:disabled {
    background-color : #cccccc;
    cursor           : not-allowed;
}
</style>

<script>
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {axiosDelete, urlGenerator} from "../../../../../common/Helper/AxiosHelper";
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import {formDataAssigner} from "../../../../../common/Helper/Support/FormHelper";
import HotelHouseKeepingImportMixins from "../../../Mixins/HotelHouseKeepingImportMixins";

export default {
    name  : "AppHotelHouseKeepingImport",
    mixins: [FormHelperMixins, HotelHouseKeepingImportMixins],
    data() {
        return {
            TENANT_BASE_URL,
            showModal  : false,
            loading    : false,
            preloader  : false,
            headers    : [],
            dataSet    : null,
            fieldValue : null,
            placeholder: this.$placeholder('file', 'csv'),
            columns    : [
                {id: 'date', label: this.$t('date')},
                {id: 'room_no', label: this.$t('room_no')},
                {id: 'primary_maid_id', label: this.$t('primary_staff')},
                {id: 'primary_maid_name', label: this.$t('primary_staff_name')},
                {id: 'assistant_maid_id', label: this.$t('assistant_staff')},
                {id: 'assistant_maid_name', label: this.$t('assistant_staff_name')},
                {id: 'staying_status', label: this.$t('staying')},
                {id: 'cleaning_status', label: this.$t('cleaning')},
                {id: 'options', label: this.$t('option')},
                {id: 'options_quantity', label: this.$t('quantity')},
                {id: 'start_time', label: this.$t('start_time')},
                {id: 'end_time', label: this.$t('end_time')},
                {id: 'remark', label: this.$t('remark')},
            ],
            formData   : {
                confirmed: false,
                hotel_id : null,
            },
            isVisible  : false,
            isImporting: false,
            message    : '',
            status     : '',
            progress   : 0,
        }
    },
    methods: {
        change(event) {
            const placeholder = this.$placeholder('file', 'csv')
            this.fieldValue   = event.target.files[0];
            this.headers      = [];

            if (this.fieldValue) {
                const reader  = new FileReader();
                reader.onload = (e) => {
                    const data   = e.target.result;
                    const header = data.split(/[\r\n]+/)[0].split(",")

                    for (const index in header) {
                        this.headers.push({
                            id   : index,
                            label: header[index],
                        })
                    }
                }

                reader.readAsText(this.fieldValue);

                this.placeholder = this.fieldValue?.name || placeholder;
            } else {
                this.placeholder = placeholder;
                this.fieldValue  = null;
            }
        },
        submitData() {
            this.loading   = true
            const url      = this.$refs.form.dataset["url"];
            const formData = formDataAssigner(new FormData, this.formData);

            formData.set('import_file', this.fieldValue)
            formData.set('columns', JSON.stringify(this.columns))
            formData.set('hotel_id', this.formData.hotel_id)
            formData.set('confirmed', this.formData.confirmed)

            this.submitFromFixin('post', url, formData);
        },
        afterSuccess({data}) {
            $('#house-keeping-import').modal('hide');
            this.$emit('input', false);
            this.toastAndReload(data.message, 'house-keeping-table');
        },
        afterSuccessFromGetEditData(response) {
            this.loading     = false;
            this.preloader   = false;
            this.fieldValue  = null;
            this.headers     = [];
            this.formData    = {};
            this.placeholder = this.$placeholder('file', 'csv');
        },
        cleanData() {
            axiosDelete(`${TENANT_BASE_URL}app/hotel/house-keeping-import/clean`).then((res) => {
                this.toastAndReload(res.data.message, 'house-keeping-table');
            }).catch((error) => {
                this.$toastr.e(error.response.data.message)
            })
        },
        syncData() {
            this.isVisible   = true;
            this.isImporting = true;
            this.message     = 'Importing in progress...';
            this.status      = '';
            this.progress    = 0;

            fetch(urlGenerator(`${TENANT_BASE_URL}app/hotel/house-keeping-import/sync`), {
                method : 'POST',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Content-Type': 'text/plain',
                }
            }).then(response => {
                if (!response.body) {
                    this.status      = 'error';
                    this.isImporting = false;
                    this.message     = "No stream data received.";
                    return;
                }
                const reader      = response.body.getReader();
                const decoder     = new TextDecoder();
                const processText = async () => {
                    while (true) {
                        const {value, done} = await reader.read();

                        if (done)
                            break;

                        const chunk = decoder.decode(value, {stream: true});

                        chunk.split(/\r?\n/).forEach(line => {
                            if (line.trim() !== '') {
                                const lineData = JSON.parse(line)
                                this.progress  = lineData.progress;
                                this.message   = lineData.message;
                            }
                        });
                    }
                    this.isImporting = false;
                };

                processText();
            }).catch(error => {
                this.message     = "❌ Connection error: " + error.message;
                this.status      = 'error';
                this.isImporting = false;
            });
        },
        closePopup() {
            this.isVisible = false;
            this.message   = '';
            this.status    = '';
            this.progress  = 0;
            this.$hub.$emit(`reload-house-keeping-table`)
        },
    }
}
</script>